<template>
	<NuxtLink :to="`/work/${item.slug}`"
			  class="card-link"
			  :style="`--details-color: ${item.details_color};`"
			  :aria-label="`Check out the ${item.title} case`">

		<baseCard class="card preview"
				  :style="`--details-color: ${item.details_color};`"
				  data-cursor-custom
				  data-cursor-text="PEEP IT"
				  @mouseenter="mouseEnter"
				  @mouseleave="mouseLeave">

			<template v-if="getFeaturedCaseImage(item)"
					  #imageSlot>
				<div ref="mediaWrapper"
					 class="image-wrapper"
					 :style="getFeaturedCaseImage(item).padding">

					<clientOnly>
						<baseVideo v-if="item.thumbnail_video && !isMobile"
								   :data="item.thumbnail_video"
								   preload
								   loop
								   autoplay
								   muted
								   playsinline />
					</clientOnly>

					<basePicture v-if="getFeaturedCaseImage(item)"
								 class="static-image"
								 :image="getFeaturedCaseImage(item)"
								 :class="{ hidden: hover && !isMobile }"
								 :sizes="sizes" />
				</div>
			</template>

			<template #contentSlot>
				<div v-if="item.client"
					 class="card-subtitle">

					<span class="subtitle-description">
						Case:
					</span>
					<span class="subtitle-content">
						{{ item.client }}
					</span>
				</div>
				<h2 class="card-title"
					v-html="item.display_title" />
			</template>

			<template #default>
				<div class="arrow">
					<div class="line" />
					<arrowTip class="point" />
				</div>
			</template>
		</baseCard>
	</NuxtLink>
</template>

<script setup>
import arrowTip from '~/assets/images/icon-caret-right.svg'

const props = defineProps({
	data: {
		type: Object,
		required: true
	},
	sizes: {
		type: String,
		required: false,
		default: null
	}
})

const isMobile = useBreakpoint('below', 'md')
const hover = ref(false)
const mediaWrapperRef = useTemplateRef('mediaWrapper')

// guards the async play() below — the pointer may have left before it resolves
let isHovering = false

const item = computed(() => props.data?.featured_case || props.data)

function videoEl() {
	return mediaWrapperRef.value?.querySelector('video') || null
}

// Safari won't autoplay at all in Low Power Mode, and holds a muted video until
// WebKit considers it visible — so a card can sit on a black frame. play() is
// rejected until the page has had a user gesture, hence the swallowed catch.
function ensurePlaying() {
	const video = videoEl()

	if (!video || !video.paused) return

	video.play().catch(() => { })
}

function getFeaturedCaseImage() {
	const image = item.value?.thumbnail_image || item.value?.featured_image

	if (image) {
		const { width, height } = image

		image.padding = `padding-top: ${(height / width) * 100}%`

		return image
	}

	return null
}
function mouseEnter(e) {
	const el = e.currentTarget

	if (isMobile.value) return

	isHovering = true

	const video = videoEl()

	// no video to reveal, or it's already running — behave as before
	if (!video || !video.paused) {
		el.classList.add('hovered')
		return
	}

	// only uncover the video once it is genuinely playing. Without this, a visitor
	// in Low Power Mode hovers and the thumbnail fades away to a black frame and a
	// play icon; leaving the static image in place degrades far more gracefully.
	video.play()
		.then(() => isHovering && el.classList.add('hovered'))
		.catch(() => { })
}

function mouseLeave(e) {
	const el = e.currentTarget

	isHovering = false
	el.classList.remove('hovered')
}

onMounted(() => {
	// baseVideo sits inside clientOnly, so it isn't in the DOM yet on this tick
	nextTick(() => {
		const video = videoEl()

		if (!video) return

		video.addEventListener('canplay', ensurePlaying)
		ensurePlaying()
	})
})

onBeforeUnmount(() => {
	videoEl()?.removeEventListener('canplay', ensurePlaying)
})
</script>

<style lang="scss" scoped>
.card-link {
	display: block;
	width: 100%;
	max-width: rem-calc(1163);
}

.card-wrapper {
	// @include standard-margin-bottom;

	.card-right {
		display: flex;
		justify-content: flex-end;
	}
}

.card {
	width: 100%;
	max-width: rem-calc(1163);
	visibility: hidden;

	&.hovered {
		// :deep(picture) {
		// 	opacity: 0;
		// }

		:deep(img) {
			opacity: 0;
		}
	}

	.image-wrapper {
		width: 100%;
		position: relative;
		@include rounded-corners;

		video {
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			height: auto;
			height: 100%;
			min-width: 100%;
			object-fit: cover;
		}

		:deep(picture) {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			transition: opacity 0.5s $easeOutQuint;
			will-change: opacity;

			img {
				position: absolute;
				top: 50%;
				left: 0;
				transform: translateY(-50%);
				height: auto;
				min-height: 100%;
				min-width: 100%;
			}

		}
	}

	:deep(.card-title) {
		@include textH4;
		text-transform: none;
		align-items: stretch;
		flex-grow: 2;
		--font-width: 100;
		margin: rem-calc(5 0 15 0);

		b,
		strong {
			font-family: "SuperStrange";
			font-variation-settings: "wdth" var(--font-width, 100);
			will-change: font-variation-settings;
		}
	}

	.card-subtitle {
		color: var(--details-color, $black);
		margin: rem-calc(24px 0px 0px 0px);
		@include textBody;

		.subtitle-content {
			font-weight: bold;
		}
	}

	.arrow {
		.line {
			background-color: var(--details-color, $black);
		}

		.point {
			color: var(--details-color, $black);
		}
	}

	&:hover {
		.arrow {
			width: calc(100% - 7px);
		}
	}
}
</style>